import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Package, Settings, Copy, ChevronDown, ChevronRight, Upload, Download, FileSpreadsheet, Building2, AlertCircle, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// F.5 — BOM save error toast diagnostics. Differentiates three error
// shapes so the user-facing toast is actionable and DevTools always
// carries the raw error for debugging:
//   (1) Backend HTTP error response with detail payload (string or
//       validation-error array) → show detail.
//   (2) No HTTP response, but error.message / error.code present (network
//       failure, axios pipeline exception) → show message + ask user to
//       open DevTools.
//   (3) No HTTP response and no message → generic "no server response".
// Always console.error the raw error for DevTools forensics.
// Caller is responsible for handling 409 bom_deletion_blocked BEFORE
// invoking this helper (that branch has structured UI treatment).
const _toastBomSaveError = (error, ctx) => {
  console.error(`[BOM save] ${ctx} error`, error);
  const errorDetail = error?.response?.data?.detail;
  if (errorDetail !== undefined && errorDetail !== null) {
    if (typeof errorDetail === 'string') {
      toast.error(errorDetail);
      return;
    }
    if (Array.isArray(errorDetail)) {
      toast.error(errorDetail.map((e) => e.msg || JSON.stringify(e)).join(', '));
      return;
    }
    toast.error('Failed to save BOM (see DevTools console for details).');
    return;
  }
  // No HTTP response received from the backend.
  const msg = error?.message || error?.code;
  if (msg) {
    toast.error(`Could not save BOM: ${msg}. Open DevTools console for details.`);
    return;
  }
  toast.error('Could not save BOM (no server response). Check network connection and try again. See DevTools console for details.');
};

export default function BOMMasterPage() {
  const { user, getAuthHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [variantGroups, setVariantGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [openClone, setOpenClone] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [editBom, setEditBom] = useState(null);
  const [selectedBom, setSelectedBom] = useState(null);
  const [expandedBom, setExpandedBom] = useState(null); // Single expanded BOM (accordion style)
  const [expandedProcesses, setExpandedProcesses] = useState({}); // Track expanded processes in form
  const [cloneForm, setCloneForm] = useState({ bom_code: '', bom_name: '', variant_name: '', client_id: '' });
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingVariants, setExistingVariants] = useState([]);
  const fileInputRef = useRef(null);
  const dialogLinkDropdownRef = useRef(null);
  
  // ---------------------------------------------------------------------
  // BOM auto-propagation pre-flight (Phase 1, 2026-05-06)
  // When editing an existing BOM, show a confirm dialog with:
  //  - list of WOs that will be propagated to (eligible)
  //  - list of WOs skipped (ineligible — JCs already issued)
  //  - any deletion blockers (issued material on a line being removed)
  // Save is blocked until blockers clear OR the user confirms.
  // ---------------------------------------------------------------------
  const [propagationDialog, setPropagationDialog] = useState({
    open: false,
    preview: null,
    blockers: [],
    pendingPayload: null,
    submitting: false,
  });

  const closePropagationDialog = () =>
    setPropagationDialog({ open: false, preview: null, blockers: [],
                           pendingPayload: null, submitting: false });

  const submitBomEditFinal = async (payload) => {
    const response = await axios.patch(`${API_URL}/bom/${editBom.id}`, payload,
      { headers: getAuthHeader() });
    const propResult = response?.data?._propagation;
    if (propResult && propResult.success === false) {
      toast.warning(`BOM saved, but propagation reported errors: ${propResult.error || 'see backend logs'}`);
    } else if (propResult && (propResult.updated_count || 0) > 0) {
      toast.success(`BOM updated. Propagated to ${propResult.updated_count} WO(s).`);
    } else {
      toast.success('BOM updated successfully');
    }
  };


  // Linked Items Management (for list page expansion)
  const [linkedItemsMap, setLinkedItemsMap] = useState({}); // {bomId: [items]}
  const [linkItemDialogOpen, setLinkItemDialogOpen] = useState(false);
  const [selectedBomForLinking, setSelectedBomForLinking] = useState(null);
  const [linkItemSearch, setLinkItemSearch] = useState('');
  const [linkingItem, setLinkingItem] = useState(false);
  
  // Linked Items Management (for Create/Edit dialog)
  const [dialogLinkedItems, setDialogLinkedItems] = useState([]); // Items linked in dialog (for edit mode)
  const [pendingLinkedItems, setPendingLinkedItems] = useState([]); // Items to link after BOM creation (for create mode)
  const [dialogLinkSearch, setDialogLinkSearch] = useState('');
  const [showDialogLinkDropdown, setShowDialogLinkDropdown] = useState(false);
  const [originalFinishedGoodId, setOriginalFinishedGoodId] = useState(''); // Track original to detect changes
  
  // Pending BOM context (when coming from Pending BOM Register)
  const [pendingBomContext, setPendingBomContext] = useState(null);
  
  // New structure: processes contain materials
  const [formData, setFormData] = useState({
    bom_code: '',
    bom_name: '',
    finished_good_id: '',
    variant_name: '',
    client_id: '',
    description: '',
    processes: [], // Each process now has a 'materials' array inside
    status: 'active'
  });

  const fetchData = async () => {
    try {
      const [bomsRes, itemsRes, processesRes, clientsRes, variantGroupsRes, suppliersRes, locationsRes] = await Promise.all([
        axios.get(`${API_URL}/bom`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/items`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/processes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/clients`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/variant-groups`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/locations`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setBoms(bomsRes.data);
      setItems(itemsRes.data);
      setProcesses(processesRes.data);
      setClients(clientsRes.data);
      setVariantGroups(variantGroupsRes.data);
      setSuppliers(suppliersRes.data);
      setLocations(locationsRes.data.filter(l => l.is_active !== false));
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  // Fetch linked items for a BOM
  const fetchLinkedItems = async (bomId) => {
    try {
      const response = await axios.get(`${API_URL}/bom/${bomId}/linked-items`, { headers: getAuthHeader() });
      setLinkedItemsMap(prev => ({ ...prev, [bomId]: response.data.linked_items || [] }));
    } catch (error) {
      console.error('Failed to fetch linked items:', error);
    }
  };

  // Link an item to a BOM
  const handleLinkItem = async (bomId, itemId) => {
    setLinkingItem(true);
    try {
      await axios.post(`${API_URL}/bom/${bomId}/link-item`, { item_id: itemId }, { headers: getAuthHeader() });
      toast.success('Item linked to BOM successfully');
      await fetchLinkedItems(bomId);
      setLinkItemSearch('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link item');
    } finally {
      setLinkingItem(false);
    }
  };

  // Unlink an item from a BOM
  const handleUnlinkItem = async (bomId, itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to unlink "${itemName}" from this BOM?`)) return;
    try {
      await axios.delete(`${API_URL}/bom/${bomId}/unlink-item/${itemId}`, { headers: getAuthHeader() });
      toast.success('Item unlinked from BOM');
      await fetchLinkedItems(bomId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink item');
    }
  };

  // Get FNG/SFG items available for linking (not already linked to another BOM)
  const getAvailableItemsForLinking = (bomId) => {
    const linkedItems = linkedItemsMap[bomId] || [];
    const linkedIds = new Set(linkedItems.map(i => i.id));
    
    return items.filter(item => {
      // Only FNG/SFG items (manufacturable categories)
      const cat = item.category?.toUpperCase();
      if (cat !== 'FNG' && cat !== 'SFG') return false;
      // Not already linked to this BOM
      if (linkedIds.has(item.id)) return false;
      // Not linked to ANY BOM (item.bom_id must be empty)
      if (item.bom_id) return false;
      // Apply search filter
      if (linkItemSearch && !item.name?.toLowerCase().includes(linkItemSearch.toLowerCase())) return false;
      return true;
    });
  };

  useEffect(() => {
    fetchData();
  }, []);
  
  // Click outside handler for dialog link dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogLinkDropdownRef.current && !dialogLinkDropdownRef.current.contains(event.target)) {
        setShowDialogLinkDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle URL params from Pending BOM Register
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    const itemId = searchParams.get('item_id');
    const itemName = searchParams.get('item_name');
    const pendingBomId = searchParams.get('pending_bom_id');
    
    if (shouldCreate && itemId && items.length > 0) {
      // Find the item to verify it exists
      const item = items.find(i => i.id === itemId);
      
      // Store pending BOM context for later use
      if (pendingBomId) {
        setPendingBomContext({
          pending_bom_id: pendingBomId,
          so_id: searchParams.get('so_id') || '',
          so_number: searchParams.get('so_number') || '',
          client_name: searchParams.get('client_name') || '',
          quantity: searchParams.get('quantity') || '',
          unit: searchParams.get('unit') || ''
        });
      }
      
      // Pre-fill form with item and auto-generate BOM code/name
      const timestamp = Date.now().toString().slice(-6);
      setFormData({
        bom_code: `BOM-${(itemName || 'ITEM').substring(0, 10).toUpperCase().replace(/\s+/g, '-')}-${timestamp}`,
        bom_name: itemName || '',
        finished_good_id: itemId,
        variant_name: '',
        client_id: '',
        description: pendingBomId ? `Created from Pending BOM Register (SO: ${searchParams.get('so_number') || 'N/A'})` : '',
        processes: [],
        status: 'active'
      });
      
      // Fetch existing variants for this item
      fetchExistingVariants(itemId);
      
      // Open the dialog
      setOpen(true);
      
      // Clear URL params to prevent re-triggering
      setSearchParams({});
    }
  }, [searchParams, items]);

  // Filter FNG/SFG items for the "Finished Good (Original)" dropdown
  // Exclude items already linked to any BOM, except the currently editing BOM's original item
  const finishedGoods = items.filter(item => {
    // Must be FNG or SFG (manufacturable categories)
    const cat = item.category?.toUpperCase();
    if (cat !== 'FNG' && cat !== 'SFG') return false;
    // If editing, allow the current BOM's finished good
    if (editBom && item.id === editBom.finished_good_id) return true;
    // Exclude items already linked to any BOM
    if (item.bom_id) return false;
    return true;
  });

  // Materials for BOM: RMT and SFG only (exclude CTM, ASSET)
  const rawMaterials = items.filter(item => {
    const cat = item.category?.toUpperCase();
    return cat === 'RMT' || cat === 'SFG';
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.finished_good_id) {
      toast.error('Please select a finished good');
      return;
    }
    
    if (formData.processes.length === 0) {
      toast.error('Please add at least one process');
      return;
    }
    
    // Validate all process and material quantities
    for (let i = 0; i < formData.processes.length; i++) {
      const p = formData.processes[i];
      const pQty = parseFloat(p.quantity_required);
      if (!p.quantity_required || isNaN(pQty) || pQty <= 0) {
        toast.error(`Process ${i + 1}: Quantity is required`);
        return;
      }
      for (let j = 0; j < (p.materials || []).length; j++) {
        const m = p.materials[j];
        const mQty = parseFloat(m.quantity_required);
        if (!m.quantity_required || isNaN(mQty) || mQty <= 0) {
          toast.error(`Process ${i + 1}, Material ${j + 1}: Quantity is required`);
          return;
        }
      }
    }

    try {
      const payload = {
        bom_code: formData.bom_code,
        bom_name: formData.bom_name,
        finished_good_id: formData.finished_good_id,
        description: formData.description,
        processes: formData.processes.map(p => ({
          id: p.id || undefined,
          process_id: p.process_id,
          process_name: p.process_name,
          quantity_required: parseFloat(p.quantity_required) || 0,
          unit: p.unit,
          is_outsourced: p.is_outsourced || false,
          jobber_id: p.jobber_id || null,
          jobber_name: p.jobber_name || null,
          unit_id: p.unit_id || null,
          unit_code: p.unit_code || null,
          unit_name: p.unit_name || null,
          materials: (p.materials || []).map(m => ({
            item_id: m.item_id || '',
            item_name: m.item_name || '',
            item_code: m.item_code || '',
            quantity_required: parseFloat(m.quantity_required) || 0,
            unit: m.unit || '',
            price: m.price || 0,
            is_variant: m.is_variant || false,
            variant_group_id: m.variant_group_id || null
          }))
        }))
      };

      let newBomId = null;
      
      if (editBom) {
        // Include all updateable fields for edit
        payload.status = formData.status;
        payload.variant_name = formData.variant_name || null;
        payload.client_id = formData.client_id || null;

        // Phase 1 propagation pre-flight: ask backend what would happen.
        // If any deletion blockers OR any open WOs would propagate, surface
        // a confirm dialog before submitting the PATCH.
        let preview = null;
        let blockers = [];
        try {
          const [pRes, dRes] = await Promise.all([
            axios.get(`${API_URL}/bom/${editBom.id}/propagation-preview`,
              { headers: getAuthHeader() }),
            axios.post(`${API_URL}/bom/${editBom.id}/propagation-deletion-check`,
              payload, { headers: getAuthHeader() }),
          ]);
          preview = pRes?.data || null;
          blockers = dRes?.data?.blockers || [];
        } catch (preflightErr) {
          // Preflight failure shouldn't block the edit — backend PATCH
          // re-runs the deletion-check authoritatively and will 409 if
          // a real blocker exists. Log + continue.
          console.warn('BOM propagation preflight failed:', preflightErr);
        }

        const totalAffected = (preview?.eligible_count || 0)
                            + (preview?.ineligible_count || 0);
        if (blockers.length > 0 || totalAffected > 0) {
          // Defer submit; user must confirm via dialog.
          setPropagationDialog({
            open: true,
            preview,
            blockers,
            pendingPayload: payload,
            submitting: false,
          });
          return;
        }

        // No open WOs and no blockers → submit directly (current path).
        await submitBomEditFinal(payload);
      } else {
        const response = await axios.post(`${API_URL}/bom`, payload, { headers: getAuthHeader() });
        newBomId = response.data?.id;
        toast.success('BOM created successfully');
        
        // Link all additional pending items (excluding the original finished good which is already linked)
        if (newBomId && pendingLinkedItems.length > 1) {
          const additionalItems = pendingLinkedItems.filter(item => item.id !== formData.finished_good_id);
          for (const item of additionalItems) {
            try {
              await axios.post(`${API_URL}/bom/${newBomId}/link-item`, { item_id: item.id }, { headers: getAuthHeader() });
            } catch (linkError) {
              console.error(`Failed to link item ${item.name}:`, linkError);
              toast.warning(`BOM created but failed to link item: ${item.name}`);
            }
          }
          if (additionalItems.length > 0) {
            toast.success(`${additionalItems.length} additional item(s) linked to BOM`);
          }
        }
      }
      
      // If this BOM was created from Pending BOM Register, mark the entry as resolved
      if (!editBom && pendingBomContext?.pending_bom_id && newBomId) {
        try {
          const result = await axios.post(
            `${API_URL}/pending-bom/${pendingBomContext.pending_bom_id}/bom-created`,
            { bom_id: newBomId },
            { headers: getAuthHeader() }
          );
          
          if (result.data?.variants_pending > 0) {
            toast.info(`Work Order created with ${result.data.variants_pending} variant(s) pending selection`);
          } else if (result.data?.wo_number) {
            toast.success(`Work Order ${result.data.wo_number} created successfully`);
          }
          
          // Clear pending BOM context
          setPendingBomContext(null);
        } catch (pendingError) {
          console.error('Failed to update pending BOM entry:', pendingError);
          toast.warning('BOM created, but failed to update Pending BOM Register. Please check manually.');
        }
      }
      
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error?.response?.data?.detail;
      // Phase 1 deletion-blocker: backend returns 409 with structured detail
      // when issued material exists on a line being deleted.
      if (
        error.response?.status === 409 &&
        errorDetail && typeof errorDetail === 'object' &&
        errorDetail.error === 'bom_deletion_blocked'
      ) {
        console.error('[BOM save] direct error', error);
        const blockerCount = (errorDetail.blockers || []).length;
        toast.error(
          `Cannot save: ${blockerCount} material line(s) have issued material on open WO(s). ` +
          `Receive or correct those JCs first.`
        );
        return;
      }
      _toastBomSaveError(error, 'direct');
    }
  };

  // Confirm-handler called from BOMPropagationConfirmDialog. Runs the
  // deferred PATCH using the payload captured at preflight time.
  const handlePropagationConfirm = async () => {
    const payload = propagationDialog.pendingPayload;
    if (!payload) return;
    setPropagationDialog((prev) => ({ ...prev, submitting: true }));
    try {
      await submitBomEditFinal(payload);
      closePropagationDialog();
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error?.response?.data?.detail;
      if (
        error.response?.status === 409 &&
        errorDetail && typeof errorDetail === 'object' &&
        errorDetail.error === 'bom_deletion_blocked'
      ) {
        console.error('[BOM save] propagation-confirm error', error);
        // Refresh blockers in the dialog so user sees the updated list.
        setPropagationDialog((prev) => ({
          ...prev,
          blockers: errorDetail.blockers || [],
          submitting: false,
        }));
        toast.error('Save blocked — issued material exists on deleted line(s). See blockers below.');
        return;
      }
      setPropagationDialog((prev) => ({ ...prev, submitting: false }));
      _toastBomSaveError(error, 'propagation-confirm');
    }
  };

  const resetForm = () => {
    setFormData({
      bom_code: '',
      bom_name: '',
      finished_good_id: '',
      variant_name: '',
      client_id: '',
      description: '',
      processes: [],
      status: 'active'
    });
    setEditBom(null);
    setExistingVariants([]);
    setExpandedProcesses({});
    setPendingBomContext(null);
    setDialogLinkedItems([]);
    setPendingLinkedItems([]);
    setDialogLinkSearch('');
    setShowDialogLinkDropdown(false);
    setOriginalFinishedGoodId('');
  };

  const handleEdit = (bom) => {
    setEditBom(bom);
    // Initialize expanded state for all processes
    const expandState = {};
    (bom.processes || []).forEach((_, idx) => {
      expandState[idx] = true;
    });
    setExpandedProcesses(expandState);
    
    setFormData({
      bom_code: bom.bom_code,
      bom_name: bom.bom_name,
      finished_good_id: bom.finished_good_id,
      variant_name: bom.variant_name || '',
      client_id: bom.client_id || '',
      description: bom.description || '',
      processes: bom.processes || [],
      status: bom.status || 'active'
    });
    
    // Store original finished_good_id to detect changes
    setOriginalFinishedGoodId(bom.finished_good_id);
    
    // Fetch linked items for this BOM
    fetchDialogLinkedItems(bom.id);
    
    fetchExistingVariants(bom.finished_good_id);
    setOpen(true);
  };
  
  // Fetch linked items for the dialog (when editing)
  const fetchDialogLinkedItems = async (bomId) => {
    if (!bomId) {
      setDialogLinkedItems([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/bom/${bomId}/linked-items`, { headers: getAuthHeader() });
      setDialogLinkedItems(response.data.linked_items || []);
    } catch (error) {
      console.error('Failed to fetch linked items for dialog:', error);
      setDialogLinkedItems([]);
    }
  };
  
  // Handle linking an item from within the dialog
  const handleDialogLinkItem = async (itemId) => {
    if (!editBom?.id) return;
    
    try {
      await axios.post(`${API_URL}/bom/${editBom.id}/link-item`, { item_id: itemId }, { headers: getAuthHeader() });
      toast.success('Item linked to BOM');
      await fetchDialogLinkedItems(editBom.id);
      setDialogLinkSearch('');
      setShowDialogLinkDropdown(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link item');
    }
  };
  
  // Handle unlinking an item from within the dialog
  const handleDialogUnlinkItem = async (itemId, itemName) => {
    if (!editBom?.id) return;
    
    // Check if this is the last item
    if (dialogLinkedItems.length <= 1) {
      toast.error('Cannot unlink the last item. A BOM must have at least one linked item.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to unlink "${itemName}" from this BOM?`)) return;
    
    try {
      await axios.delete(`${API_URL}/bom/${editBom.id}/unlink-item/${itemId}`, { headers: getAuthHeader() });
      toast.success('Item unlinked from BOM');
      await fetchDialogLinkedItems(editBom.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink item');
    }
  };
  
  // Handle adding item to pending list (Create mode)
  const handleAddPendingLinkedItem = (item) => {
    if (pendingLinkedItems.some(i => i.id === item.id)) return;
    setPendingLinkedItems([...pendingLinkedItems, item]);
    setDialogLinkSearch('');
    setShowDialogLinkDropdown(false);
  };
  
  // Handle removing item from pending list (Create mode)
  const handleRemovePendingLinkedItem = (itemId) => {
    // Check if this is the original finished good
    if (formData.finished_good_id === itemId) {
      toast.error('Cannot remove the original finished good. Change the "Finished Good (Original)" field instead.');
      return;
    }
    setPendingLinkedItems(pendingLinkedItems.filter(i => i.id !== itemId));
  };
  
  // Get available items for linking in dialog (not already linked to ANY BOM)
  const getDialogAvailableItems = () => {
    // For edit mode: use dialogLinkedItems, for create mode: use pendingLinkedItems
    const currentLinkedIds = editBom 
      ? new Set(dialogLinkedItems.map(i => i.id))
      : new Set(pendingLinkedItems.map(i => i.id));
    
    return items.filter(item => {
      // Only FNG/SFG items (manufacturable categories)
      const cat = item.category?.toUpperCase();
      if (cat !== 'FNG' && cat !== 'SFG') return false;
      // Not already in current linked list
      if (currentLinkedIds.has(item.id)) return false;
      // Not linked to ANY BOM (item.bom_id must be empty/null)
      if (item.bom_id) return false;
      // Apply search filter
      if (dialogLinkSearch && !item.name?.toLowerCase().includes(dialogLinkSearch.toLowerCase())) return false;
      return true;
    }).slice(0, 20); // Limit for performance
  };

  const fetchExistingVariants = async (finishedGoodId) => {
    if (!finishedGoodId) {
      setExistingVariants([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/bom/variants/${finishedGoodId}`, { headers: getAuthHeader() });
      setExistingVariants(response.data);
    } catch {
      setExistingVariants([]);
    }
  };

  const handleFinishedGoodChange = (value) => {
    setFormData({ ...formData, finished_good_id: value });
    fetchExistingVariants(value);
    
    // In create mode, add/update the original item in pendingLinkedItems
    if (!editBom && value) {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem) {
        // Replace any existing original item or add new one
        setPendingLinkedItems(prev => {
          // Remove old original (if any) and add new one
          const withoutOldOriginal = prev.filter(i => i.id !== formData.finished_good_id);
          // Check if new item is already in the list
          if (withoutOldOriginal.some(i => i.id === value)) {
            return withoutOldOriginal;
          }
          return [{ ...selectedItem, is_original: true }, ...withoutOldOriginal.map(i => ({ ...i, is_original: false }))];
        });
      }
    }
  };

  const handleClone = async () => {
    if (!cloneForm.bom_code || !cloneForm.bom_name) {
      toast.error('Please enter BOM code and name');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        new_bom_code: cloneForm.bom_code,
        new_bom_name: cloneForm.bom_name
      });
      if (cloneForm.variant_name) params.append('variant_name', cloneForm.variant_name);
      if (cloneForm.client_id) params.append('client_id', cloneForm.client_id);
      
      await axios.post(`${API_URL}/bom/${selectedBom.id}/clone?${params}`, {}, { headers: getAuthHeader() });
      toast.success('BOM cloned successfully');
      setOpenClone(false);
      setCloneForm({ bom_code: '', bom_name: '', variant_name: '', client_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone BOM');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/bom/${selectedBom.id}`, { headers: getAuthHeader() });
      toast.success('BOM deleted successfully');
      setOpenDelete(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete BOM');
    }
  };

  // ============= PROCESS FUNCTIONS =============
  
  const addProcess = () => {
    const newIdx = formData.processes.length;
    setFormData({
      ...formData,
      processes: [...formData.processes, { 
        process_id: '', 
        process_name: '', 
        quantity_required: '', 
        unit: '', 
        is_outsourced: false,
        jobber_id: '',
        jobber_name: '',
        unit_id: '',
        unit_code: '',
        unit_name: '',
        materials: [] // Materials nested inside process
      }]
    });
    // Auto-expand new process
    setExpandedProcesses(prev => ({ ...prev, [newIdx]: true }));
  };

  const removeProcess = (index) => {
    setFormData({
      ...formData,
      processes: formData.processes.filter((_, i) => i !== index)
    });
  };

  const updateProcess = (index, field, value) => {
    const updated = [...formData.processes];
    if (field === 'process_id') {
      const selectedProcess = processes.find(p => p.id === value);
      if (selectedProcess) {
        updated[index] = {
          ...updated[index],
          process_id: value,
          process_name: selectedProcess.name,
          unit: selectedProcess.unit
        };
      }
    } else if (field === 'jobber_id') {
      const selectedSupplier = suppliers.find(s => s.id === value);
      updated[index] = { 
        ...updated[index], 
        jobber_id: value,
        jobber_name: selectedSupplier?.name || ''
      };
    } else if (field === 'unit_id') {
      const selectedLocation = locations.find(l => l.id === value);
      updated[index] = { 
        ...updated[index], 
        unit_id: value,
        unit_code: selectedLocation?.code || '',
        unit_name: selectedLocation?.name || ''
      };
    } else if (field === 'is_outsourced') {
      updated[index] = { 
        ...updated[index], 
        is_outsourced: value,
        ...(value === false && { jobber_id: '', jobber_name: '' })
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setFormData({ ...formData, processes: updated });
  };

  // ============= MATERIAL FUNCTIONS (Nested inside Process) =============
  
  const addMaterialToProcess = (processIndex) => {
    const updated = [...formData.processes];
    updated[processIndex].materials = [
      ...(updated[processIndex].materials || []),
      { 
        item_id: '', 
        item_name: '', 
        item_code: '',
        quantity_required: '', 
        unit: '', 
        price: 0,
        is_variant: false,
        variant_group_id: null
      }
    ];
    setFormData({ ...formData, processes: updated });
  };

  const removeMaterialFromProcess = (processIndex, materialIndex) => {
    const updated = [...formData.processes];
    updated[processIndex].materials = updated[processIndex].materials.filter((_, i) => i !== materialIndex);
    setFormData({ ...formData, processes: updated });
  };

  const updateMaterialInProcess = (processIndex, materialIndex, field, value) => {
    const updated = [...formData.processes];
    const materials = [...(updated[processIndex].materials || [])];
    
    if (field === 'is_variant') {
      materials[materialIndex] = {
        ...materials[materialIndex],
        is_variant: value,
        item_id: value ? '' : materials[materialIndex].item_id,
        item_name: value ? '' : materials[materialIndex].item_name,
        variant_group_id: value ? '' : null,
        price: 0
      };
    } else if (field === 'variant_group_id') {
      const group = variantGroups.find(g => g.id === value);
      materials[materialIndex] = {
        ...materials[materialIndex],
        variant_group_id: value,
        item_name: group ? `[${group.name}]` : '',
        unit: group?.unit || 'pcs'
      };
    } else if (field === 'item_id') {
      const selectedItem = rawMaterials.find(item => item.id === value);
      if (selectedItem) {
        materials[materialIndex] = {
          ...materials[materialIndex],
          item_id: value,
          item_name: selectedItem.name,
          item_code: selectedItem.item_code || '',
          unit: selectedItem.unit,
          price: selectedItem.price || 0
        };
      }
    } else {
      materials[materialIndex] = { ...materials[materialIndex], [field]: value };
    }
    
    updated[processIndex].materials = materials;
    setFormData({ ...formData, processes: updated });
  };

  const toggleProcessExpand = (index) => {
    setExpandedProcesses(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Calculate total materials from all processes
  const calculateTotalMaterials = () => {
    const totals = {};
    formData.processes.forEach(process => {
      (process.materials || []).forEach(mat => {
        if (mat.item_id && !mat.is_variant) {
          if (!totals[mat.item_id]) {
            totals[mat.item_id] = {
              item_name: mat.item_name,
              item_code: mat.item_code,
              unit: mat.unit,
              quantity: 0,
              price: mat.price || 0
            };
          }
          totals[mat.item_id].quantity += parseFloat(mat.quantity_required) || 0;
        }
      });
    });
    return Object.entries(totals).map(([id, data]) => ({ item_id: id, ...data }));
  };

  const toggleExpand = (bomId) => {
    const willExpand = expandedBom !== bomId;
    setExpandedBom(willExpand ? bomId : null);
    
    // Fetch linked items when expanding
    if (willExpand && !linkedItemsMap[bomId]) {
      fetchLinkedItems(bomId);
    }
  };

  const openCloneDialog = (bom) => {
    setSelectedBom(bom);
    setCloneForm({ bom_code: `${bom.bom_code}-COPY`, bom_name: `${bom.bom_name} (Copy)` });
    setOpenClone(true);
  };

  const openDeleteDialog = (bom) => {
    setSelectedBom(bom);
    setOpenDelete(true);
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
    }
    return <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactive</Badge>;
  };

  // Aggregate all materials from BOM processes for display
  const getBOMMaterialsSummary = (bom) => {
    const totals = {};
    (bom.processes || []).forEach(process => {
      (process.materials || []).forEach(mat => {
        if (mat.item_id) {
          if (!totals[mat.item_id]) {
            totals[mat.item_id] = {
              item_name: mat.item_name,
              item_code: mat.item_code,
              unit: mat.unit,
              quantity: 0,
              is_variant: mat.is_variant
            };
          }
          totals[mat.item_id].quantity += parseFloat(mat.quantity_required) || 0;
        }
      });
    });
    return Object.entries(totals).map(([id, data]) => ({ item_id: id, ...data }));
  };

  // Download BOM bulk upload template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/bom/template/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bom_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/bom/bulk-upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadResult(response.data);
      
      if (response.data.status === 'success') {
        const created = response.data.boms_created || 0;
        const updated = response.data.boms_updated || 0;
        const errors = response.data.error_count || 0;
        
        if (errors === 0) {
          toast.success(`Successfully processed! Created: ${created}, Updated: ${updated}`);
        } else {
          toast.warning(`Processed with ${errors} errors. Created: ${created}, Updated: ${updated}`);
        }
        fetchData(); // Refresh BOM list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploadResult({ status: 'error', errors: [error.response?.data?.detail || 'Unknown error'] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">Bill of Materials Register - Define processes and materials for finished goods</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <Button variant="outline" onClick={() => { setOpenUpload(true); setUploadResult(null); }}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => { resetForm(); setOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              New BOM
            </Button>
          </div>
        </div>

        {/* BOM List */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>BOM Code</TableHead>
                  <TableHead>BOM Name</TableHead>
                  <TableHead>Finished Good</TableHead>
                  <TableHead>Processes</TableHead>
                  <TableHead>Est. Cost</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {boms.map(bom => (
                  <React.Fragment key={bom.id}>
                    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleExpand(bom.id)}>
                      <TableCell>
                        {expandedBom === bom.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </TableCell>
                      <TableCell className="font-medium">{bom.bom_code}</TableCell>
                      <TableCell>
                        {bom.bom_name}
                        {bom.variant_name && <span className="ml-2 text-xs text-muted-foreground">({bom.variant_name})</span>}
                      </TableCell>
                      <TableCell>{bom.finished_good_name}</TableCell>
                      <TableCell>{bom.processes?.length || 0}</TableCell>
                      <TableCell>₹{(bom.est_cost || 0).toLocaleString()}</TableCell>
                      <TableCell>{getStatusBadge(bom.status)}</TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(bom)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openCloneDialog(bom)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => openDeleteDialog(bom)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded BOM Details */}
                    {expandedBom === bom.id && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-muted/30 p-4">
                          <div className="grid grid-cols-2 gap-6">
                            {/* Processes with Materials */}
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Settings className="w-4 h-4" />
                                Processes &amp; Materials
                              </h4>
                              {(bom.processes || []).map((process, pIdx) => (
                                <div key={pIdx} className="mb-3 border rounded p-3 bg-white">
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="font-medium">{pIdx + 1}. {process.process_name}</span>
                                    <span className="text-sm text-muted-foreground">{process.quantity_required} {process.unit}</span>
                                  </div>
                                  {process.unit_name && (
                                    <div className="text-xs text-blue-600 mb-1">
                                      <Building2 className="w-3 h-3 inline mr-1" />
                                      {process.unit_name}
                                    </div>
                                  )}
                                  {process.is_outsourced && (
                                    <div className="text-xs text-orange-600 mb-1">Outsourced to: {process.jobber_name}</div>
                                  )}
                                  {/* Materials for this process */}
                                  {(process.materials || []).length > 0 && (
                                    <div className="mt-2 pl-4 border-l-2 border-gray-200">
                                      <div className="text-xs text-muted-foreground mb-1">Materials:</div>
                                      {process.materials.map((mat, mIdx) => (
                                        <div key={mIdx} className="text-sm flex justify-between">
                                          <span>{mat.item_name}</span>
                                          <span className="text-muted-foreground">{mat.quantity_required} {mat.unit}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            
                            {/* Aggregated Materials Summary */}
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                Total Materials Required
                              </h4>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Material</TableHead>
                                    <TableHead className="text-right">Total Qty</TableHead>
                                    <TableHead>Unit</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {getBOMMaterialsSummary(bom).map((mat, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="py-1">
                                        {mat.is_variant ? (
                                          <span className="text-purple-600">{mat.item_name}</span>
                                        ) : mat.item_name}
                                      </TableCell>
                                      <TableCell className="text-right py-1">{mat.quantity}</TableCell>
                                      <TableCell className="py-1">{mat.unit}</TableCell>
                                    </TableRow>
                                  ))}
                                  {getBOMMaterialsSummary(bom).length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                                        No materials defined
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                          
                          {/* Linked Items Section */}
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                Linked Finished Goods ({linkedItemsMap[bom.id]?.length || 0})
                              </h4>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedBomForLinking(bom);
                                  setLinkItemSearch('');
                                  setLinkItemDialogOpen(true);
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Link Item
                              </Button>
                            </div>
                            
                            {/* Linked Items List */}
                            {(linkedItemsMap[bom.id] || []).length > 0 ? (
                              <div className="space-y-2">
                                {linkedItemsMap[bom.id].map((item) => (
                                  <div 
                                    key={item.id} 
                                    className={`flex items-center justify-between p-2 rounded border ${
                                      item.is_original ? 'bg-blue-50 border-blue-200' : 'bg-white'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{item.name}</span>
                                      {item.is_original && (
                                        <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">Original</Badge>
                                      )}
                                      {item.is_variant_template && (
                                        <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700">Template</Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-muted-foreground">
                                        Stock: {item.stock_in_hand || 0}
                                      </span>
                                      {!item.is_original && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                          onClick={() => handleUnlinkItem(bom.id, item.id, item.name)}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                No items linked. Click "Link Item" to add finished goods.
                              </p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {boms.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No BOMs created yet. Click &quot;New BOM&quot; to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create/Edit BOM Dialog */}
        <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetForm(); setOpen(isOpen); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editBom ? 'Edit BOM' : 'Create New BOM'}</DialogTitle>
              <DialogDescription>Define processes and materials for a finished good</DialogDescription>
            </DialogHeader>
            
            {/* Info banner when coming from Pending BOM Register */}
            {pendingBomContext && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Creating BOM for Pending Entry</p>
                  <p className="text-blue-700">
                    SO: {pendingBomContext.so_number || 'N/A'} | 
                    Client: {pendingBomContext.client_name || 'N/A'} | 
                    Qty: {pendingBomContext.quantity} {pendingBomContext.unit}
                  </p>
                  <p className="text-blue-600 text-xs mt-1">
                    After BOM creation, a Work Order will be automatically generated.
                  </p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleCreate}>
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>BOM Code *</Label>
                    <Input
                      value={formData.bom_code}
                      onChange={(e) => setFormData({ ...formData, bom_code: e.target.value })}
                      placeholder="e.g., BOM-001"
                      required
                      disabled={!!editBom}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>BOM Name *</Label>
                    <Input
                      value={formData.bom_name}
                      onChange={(e) => setFormData({ ...formData, bom_name: e.target.value })}
                      placeholder="e.g., Wooden Table Standard"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Finished Good (Original) *</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <SearchableSelect
                          options={finishedGoods.map(item => ({ 
                            value: item.id, 
                            label: item.name,
                            code: item.item_code || '',
                            category: item.category || ''
                          }))}
                          value={formData.finished_good_id}
                          onValueChange={handleFinishedGoodChange}
                          placeholder="Select finished good"
                          disabled={!!pendingBomContext}
                        />
                      </div>
                      {editBom && formData.finished_good_id !== originalFinishedGoodId && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          Changed
                        </Badge>
                      )}
                    </div>
                    {editBom && formData.finished_good_id !== originalFinishedGoodId && (
                      <p className="text-xs text-amber-600">
                        Changing the original item will update the BOM's primary finished good.
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Linked Finished Goods Section - For both Create and Edit modes */}
                {(formData.finished_good_id || editBom) && (
                  <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Linked Finished Goods ({editBom ? dialogLinkedItems.length : pendingLinkedItems.length})
                      </Label>
                    </div>
                    
                    {/* List of linked items with inline add */}
                    <div className="space-y-2">
                      {/* For Edit mode: show dialogLinkedItems */}
                      {editBom && dialogLinkedItems.map((item) => (
                        <div 
                          key={item.id} 
                          className={`flex items-center justify-between p-2 rounded border ${
                            item.is_original ? 'bg-blue-50 border-blue-200' : 'bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{item.name}</span>
                            {item.is_original && (
                              <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">Original</Badge>
                            )}
                            {item.is_variant_template && (
                              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700">Template</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Stock: {item.stock_in_hand || 0}
                            </span>
                            {!item.is_original && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleDialogUnlinkItem(item.id, item.name)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* For Create mode: show pendingLinkedItems */}
                      {!editBom && pendingLinkedItems.map((item) => (
                        <div 
                          key={item.id} 
                          className={`flex items-center justify-between p-2 rounded border ${
                            item.id === formData.finished_good_id ? 'bg-blue-50 border-blue-200' : 'bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{item.name}</span>
                            {item.id === formData.finished_good_id && (
                              <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">Original</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Stock: {item.stock_in_hand || 0}
                            </span>
                            {item.id !== formData.finished_good_id && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleRemovePendingLinkedItem(item.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Add new item row */}
                      <div className="relative" ref={dialogLinkDropdownRef}>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search FNG/SFG item to link..."
                              value={dialogLinkSearch}
                              onChange={(e) => {
                                setDialogLinkSearch(e.target.value);
                                setShowDialogLinkDropdown(true);
                              }}
                              onFocus={() => setShowDialogLinkDropdown(true)}
                              className="pl-8 h-9"
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-9"
                            onClick={() => setShowDialogLinkDropdown(!showDialogLinkDropdown)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Dropdown for search results */}
                        {showDialogLinkDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                            {getDialogAvailableItems().length > 0 ? (
                              getDialogAvailableItems().map((item) => (
                                <div
                                  key={item.id}
                                  className="flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer"
                                  onClick={() => editBom ? handleDialogLinkItem(item.id) : handleAddPendingLinkedItem(item)}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">{item.name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {item.category}
                                    </Badge>
                                  </div>
                                  <Plus className="w-4 h-4 text-green-600" />
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                                {dialogLinkSearch ? 'No matching items found' : 'Type to search for items'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        Link multiple FNG/SFG items to use this BOM for Work Order creation.
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Variant Name</Label>
                    <Input
                      value={formData.variant_name}
                      onChange={(e) => setFormData({ ...formData, variant_name: e.target.value })}
                      placeholder="e.g., Premium, Standard"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client (Optional)</Label>
                    <SearchableSelect
                      options={clients.map(c => ({ value: c.id, label: c.name }))}
                      value={formData.client_id}
                      onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                      placeholder="Select client (if client-specific)"
                    />
                  </div>
                </div>

                {/* Processes Section with Nested Materials */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-base font-semibold">Processes &amp; Materials</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addProcess}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Process
                    </Button>
                  </div>

                  {formData.processes.length === 0 && (
                    <div className="text-center py-8 border rounded-lg bg-muted/30">
                      <Settings className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No processes added yet</p>
                      <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addProcess}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add First Process
                      </Button>
                    </div>
                  )}

                  {formData.processes.map((process, pIdx) => (
                    <Card key={pIdx} className="border-2">
                      <Collapsible open={expandedProcesses[pIdx] !== false} onOpenChange={() => toggleProcessExpand(pIdx)}>
                        <CardHeader className="py-3 px-4">
                          <div className="flex items-center justify-between">
                            <CollapsibleTrigger className="flex items-center gap-2 hover:text-primary">
                              {expandedProcesses[pIdx] !== false ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <span className="font-medium">
                                Process {pIdx + 1}: {process.process_name || '(Select process)'}
                              </span>
                              {(process.materials || []).length > 0 && (
                                <Badge variant="secondary" className="ml-2">
                                  {process.materials.length} material(s)
                                </Badge>
                              )}
                            </CollapsibleTrigger>
                            <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => removeProcess(pIdx)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        
                        <CollapsibleContent>
                          <CardContent className="pt-0 space-y-4">
                            {/* Process Details */}
                            <div className="grid grid-cols-4 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Process *</Label>
                                <SearchableSelect
                                  options={processes.map(p => ({ value: p.id, label: p.name }))}
                                  value={process.process_id}
                                  onValueChange={(v) => updateProcess(pIdx, 'process_id', v)}
                                  placeholder="Select"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Quantity *</Label>
                                <Input
                                  type="number"
                                  value={process.quantity_required}
                                  onChange={(e) => updateProcess(pIdx, 'quantity_required', e.target.value)}
                                  min="0"
                                  step="any"
                                  className="w-32"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Unit</Label>
                                <Input value={process.unit || ''} disabled className="bg-muted" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Location</Label>
                                <Select value={process.unit_id || ''} onValueChange={(v) => updateProcess(pIdx, 'unit_id', v)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {locations.map(loc => (
                                      <SelectItem key={loc.id} value={loc.id}>{loc.code} - {loc.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Outsourced Toggle */}
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={process.is_outsourced}
                                  onCheckedChange={(v) => updateProcess(pIdx, 'is_outsourced', v)}
                                />
                                <Label className="text-sm">Outsourced (OJC)</Label>
                              </div>
                              {process.is_outsourced && (
                                <div className="flex-1">
                                  <SearchableSelect
                                    options={suppliers.filter(s => s.is_active !== false).map(s => ({ value: s.id, label: s.name }))}
                                    value={process.jobber_id}
                                    onValueChange={(v) => updateProcess(pIdx, 'jobber_id', v)}
                                    placeholder="Select jobber/vendor"
                                  />
                                </div>
                              )}
                            </div>

                            {/* Materials for this Process */}
                            <div className="border-t pt-3">
                              <div className="flex justify-between items-center mb-2">
                                <Label className="text-sm font-medium flex items-center gap-2">
                                  <Package className="w-4 h-4" />
                                  Materials for this Process
                                </Label>
                                <Button type="button" variant="outline" size="sm" onClick={() => addMaterialToProcess(pIdx)}>
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Material
                                </Button>
                              </div>

                              {(process.materials || []).length === 0 ? (
                                <div className="text-center py-4 text-sm text-muted-foreground bg-muted/30 rounded">
                                  No materials added for this process
                                </div>
                              ) : (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Material</TableHead>
                                      <TableHead className="w-24">Qty</TableHead>
                                      <TableHead className="w-20">Unit</TableHead>
                                      <TableHead className="w-10"></TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {process.materials.map((mat, mIdx) => (
                                      <TableRow key={mIdx}>
                                        <TableCell className="py-1">
                                          <div className="flex items-center gap-2">
                                            <Checkbox
                                              checked={mat.is_variant}
                                              onCheckedChange={(v) => updateMaterialInProcess(pIdx, mIdx, 'is_variant', v)}
                                              title="Variant Material"
                                            />
                                            {mat.is_variant ? (
                                              <Select
                                                value={mat.variant_group_id || ''}
                                                onValueChange={(v) => updateMaterialInProcess(pIdx, mIdx, 'variant_group_id', v)}
                                              >
                                                <SelectTrigger className="h-8">
                                                  <SelectValue placeholder="Select variant group" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {variantGroups.map(g => (
                                                    <SelectItem key={g.id} value={g.id}>[{g.name}]</SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <SearchableSelect
                                                options={rawMaterials.map(item => ({ 
                                                  value: item.id, 
                                                  label: item.name,
                                                  code: item.item_code || '',
                                                  category: item.category || ''
                                                }))}
                                                value={mat.item_id}
                                                onValueChange={(v) => updateMaterialInProcess(pIdx, mIdx, 'item_id', v)}
                                                placeholder="Select material"
                                                className="flex-1"
                                              />
                                            )}
                                          </div>
                                        </TableCell>
                                        <TableCell className="py-1">
                                          <Input
                                            type="number"
                                            className="h-8 w-32"
                                            value={mat.quantity_required}
                                            onChange={(e) => updateMaterialInProcess(pIdx, mIdx, 'quantity_required', e.target.value)}
                                            min="0"
                                            step="any"
                                          />
                                        </TableCell>
                                        <TableCell className="py-1 text-sm text-muted-foreground">
                                          {mat.unit}
                                        </TableCell>
                                        <TableCell className="py-1">
                                          <Button type="button" variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => removeMaterialFromProcess(pIdx, mIdx)}>
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  ))}
                </div>

                {/* Materials Summary */}
                {formData.processes.length > 0 && calculateTotalMaterials().length > 0 && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Total Materials Summary (Aggregated)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Material</TableHead>
                            <TableHead className="text-right">Total Qty</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead className="text-right">Est. Cost</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {calculateTotalMaterials().map((mat, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="py-1">{mat.item_name}</TableCell>
                              <TableCell className="py-1 text-right">{mat.quantity}</TableCell>
                              <TableCell className="py-1">{mat.unit}</TableCell>
                              <TableCell className="py-1 text-right">₹{(mat.quantity * mat.price).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="font-semibold bg-blue-100">
                            <TableCell colSpan={3}>Total Estimated Cost</TableCell>
                            <TableCell className="text-right">
                              ₹{calculateTotalMaterials().reduce((sum, m) => sum + (m.quantity * m.price), 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Status (for edit) */}
                {editBom && (
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editBom ? 'Update BOM' : 'Create BOM'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Clone Dialog */}
        <Dialog open={openClone} onOpenChange={setOpenClone}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clone BOM</DialogTitle>
              <DialogDescription>Create a copy of {selectedBom?.bom_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New BOM Code *</Label>
                <Input
                  value={cloneForm.bom_code}
                  onChange={(e) => setCloneForm({ ...cloneForm, bom_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>New BOM Name *</Label>
                <Input
                  value={cloneForm.bom_name}
                  onChange={(e) => setCloneForm({ ...cloneForm, bom_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Variant Name</Label>
                <Input
                  value={cloneForm.variant_name}
                  onChange={(e) => setCloneForm({ ...cloneForm, variant_name: e.target.value })}
                  placeholder="e.g., Premium, Client-specific"
                />
              </div>
              <div className="space-y-2">
                <Label>Client (Optional)</Label>
                <SearchableSelect
                  options={clients.map(c => ({ value: c.id, label: c.name }))}
                  value={cloneForm.client_id}
                  onValueChange={(v) => setCloneForm({ ...cloneForm, client_id: v })}
                  placeholder="Select client"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenClone(false)}>Cancel</Button>
              <Button onClick={handleClone}>Clone BOM</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDelete} onOpenChange={setOpenDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete BOM</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{selectedBom?.bom_name}&quot;? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={openUpload} onOpenChange={setOpenUpload}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Bulk Upload BOMs</DialogTitle>
              <DialogDescription>
                Upload an Excel file to create new BOMs or add processes/materials to existing ones.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Template Download Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Template Format
                </h4>
                <p className="text-sm text-blue-800 mb-2">
                  The template uses a hierarchical single-sheet format:
                </p>
                <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                  <li>Each row represents a Process + optional Material combination</li>
                  <li>Repeat BOM Code for all rows of the same BOM</li>
                  <li>Repeat Process Name for multiple materials under that process</li>
                  <li>Leave Material columns empty for process-only rows</li>
                  <li>Finished goods & processes must exist in the system</li>
                </ul>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-blue-600 mt-2"
                  onClick={handleDownloadTemplate}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Template with Examples
                </Button>
              </div>

              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="bom-upload-input"
                />
                <label 
                  htmlFor="bom-upload-input" 
                  className="cursor-pointer block"
                >
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm font-medium">
                    {uploading ? 'Uploading...' : 'Click to select Excel file'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports .xlsx and .xls formats
                  </p>
                </label>
              </div>

              {/* Upload Results */}
              {uploadResult && (
                <div className={`rounded-lg p-4 ${
                  uploadResult.error_count > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    uploadResult.error_count > 0 ? 'text-yellow-900' : 'text-green-900'
                  }`}>
                    Upload Results
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-muted-foreground">BOMs Created:</span>{' '}
                      <span className="font-medium">{uploadResult.boms_created || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">BOMs Updated:</span>{' '}
                      <span className="font-medium">{uploadResult.boms_updated || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Processes Added:</span>{' '}
                      <span className="font-medium">{uploadResult.processes_added || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Materials Added:</span>{' '}
                      <span className="font-medium">{uploadResult.materials_added || 0}</span>
                    </div>
                  </div>
                  
                  {uploadResult.bom_codes_processed?.length > 0 && (
                    <div className="mb-3">
                      <span className="text-sm text-muted-foreground">Processed BOMs: </span>
                      <span className="text-sm font-medium">
                        {uploadResult.bom_codes_processed.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-yellow-200">
                      <p className="text-sm font-medium text-yellow-900 mb-1">
                        Errors ({uploadResult.errors.length}):
                      </p>
                      <ul className="text-xs text-yellow-800 max-h-32 overflow-y-auto space-y-1">
                        {uploadResult.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenUpload(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Item to BOM Dialog */}
        <Dialog open={linkItemDialogOpen} onOpenChange={setLinkItemDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Link Item to BOM</DialogTitle>
              <DialogDescription>
                Link a Finished Good or Semi-Finished Good to "{selectedBomForLinking?.bom_name}"
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search items by name..."
                  value={linkItemSearch}
                  onChange={(e) => setLinkItemSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              {/* Available Items List */}
              <div className="max-h-[300px] overflow-y-auto border rounded-md">
                {selectedBomForLinking && getAvailableItemsForLinking(selectedBomForLinking.id).length > 0 ? (
                  <div className="divide-y">
                    {getAvailableItemsForLinking(selectedBomForLinking.id).slice(0, 50).map((item) => (
                      <div 
                        key={item.id} 
                        className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer"
                        onClick={() => handleLinkItem(selectedBomForLinking.id, item.id)}
                      >
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.category} | Stock: {item.stock_in_hand || 0}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" disabled={linkingItem}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {linkItemSearch 
                      ? 'No matching items found' 
                      : 'No FNG/SFG items available for linking'}
                  </p>
                )}
              </div>
              
              <p className="text-xs text-muted-foreground">
                Only FNG/SFG items that are not already linked to another BOM are shown.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkItemDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* BOM auto-propagation confirm dialog (Phase 1, 2026-05-06) */}
        <Dialog
          open={propagationDialog.open}
          onOpenChange={(isOpen) => { if (!isOpen) closePropagationDialog(); }}
        >
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                         data-testid="bom-propagation-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Confirm BOM Edit — Open Work Orders Will Be Updated</DialogTitle>
              <DialogDescription>
                This BOM has open Work Orders. Saving will propagate the new
                material structure to eligible WOs and skip those with JCs
                already issued.
              </DialogDescription>
            </DialogHeader>

            {/* Blockers (deletion of a line with issued material) */}
            {propagationDialog.blockers.length > 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 mb-3"
                   data-testid="bom-propagation-blockers">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <p className="text-sm font-semibold text-red-800">
                    Save blocked — {propagationDialog.blockers.length} material
                    line(s) have issued material on open WO(s)
                  </p>
                </div>
                <ul className="text-xs text-red-700 space-y-1 ml-6 list-disc">
                  {propagationDialog.blockers.map((b, idx) => (
                    <li key={idx} data-testid={`bom-blocker-row-${idx}`}>
                      <span className="font-mono">{b.work_order_number}</span>
                      {' • '}<strong>{b.item_name}</strong>{' '}
                      has {b.issued_quantity} issued — cannot delete this line.
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-red-700 mt-2">
                  Receive or correct those JCs before re-saving.
                </p>
              </div>
            )}

            {/* Eligible WOs (will propagate) */}
            {propagationDialog.preview?.eligible_wos?.length > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 mb-3">
                <p className="text-sm font-semibold text-emerald-800 mb-2">
                  Will propagate to {propagationDialog.preview.eligible_count} WO(s)
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {propagationDialog.preview.eligible_wos.map((w) => (
                    <div key={w.work_order_id}
                         className="border border-emerald-200 bg-white rounded p-2 text-xs"
                         data-testid={`bom-eligible-wo-${w.work_order_number}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold">{w.work_order_number}</span>
                        <span className="text-muted-foreground">qty: {w.wo_quantity}</span>
                      </div>
                      {w.material_changes?.length > 0 && (
                        <ul className="ml-3 mt-1 list-disc text-muted-foreground">
                          {w.material_changes.slice(0, 5).map((c, ci) => (
                            <li key={ci}>
                              <span className="capitalize">{c.change_type}</span>
                              {': '}{c.item_name || c.new_item_name || c.old_item_name}
                              {c.old_quantity != null && c.new_quantity != null && (
                                <> {c.old_quantity} → {c.new_quantity}</>
                              )}
                            </li>
                          ))}
                          {w.material_changes.length > 5 && (
                            <li>+ {w.material_changes.length - 5} more change(s)</li>
                          )}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ineligible WOs (skipped) */}
            {propagationDialog.preview?.ineligible_wos?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-3">
                <p className="text-sm font-semibold text-amber-800 mb-2">
                  Skipped {propagationDialog.preview.ineligible_count} WO(s) — JCs already issued
                </p>
                <ul className="text-xs text-amber-700 space-y-1 ml-3 list-disc">
                  {propagationDialog.preview.ineligible_wos.map((w) => (
                    <li key={w.work_order_id}
                        data-testid={`bom-ineligible-wo-${w.work_order_number}`}>
                      <span className="font-mono">{w.work_order_number}</span> — {w.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closePropagationDialog}
                      data-testid="bom-propagation-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handlePropagationConfirm}
                disabled={propagationDialog.blockers.length > 0 || propagationDialog.submitting}
                data-testid="bom-propagation-confirm-btn"
              >
                {propagationDialog.submitting ? 'Saving...' : 'Confirm & Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
